/* eslint-disable react/no-array-index-key */
import React, { ChangeEvent, memo, useEffect } from "react";

import { useFormContext } from "react-hook-form";
import styled from "styled-components";

import { Box, Text, TextLink } from "@origin-digital/ods-core";

import { InstallerCheckbox } from "@/features/ServiceProviderSelection/InstallerCheckbox";
import * as content from "@/features/ServiceProviderSelection/content.json";

const FlexBox = styled(Box)`
  && {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    margin-left: -6px;
    margin-right: -6px;
  }
`;

export interface ServiceProvider {
  id: string;
}

interface ServiceProviderProps {
  serviceProviderDetails: ServiceProvider[];
}

interface SelectedProvider {
  id: string;
}

export const ServiceProvider = memo(
  ({ serviceProviderDetails }: ServiceProviderProps) => {
    const { getValues, setValue } = useFormContext();

    useEffect(() => {
      const currentSelected = getValues("selectedServiceProviders");
      if (!currentSelected || currentSelected.length === 0) {
        const initSelectedServiceProviders = serviceProviderDetails.map(
          (item) => ({
            id: item.id,
          })
        );
        setValue("selectedServiceProviders", initSelectedServiceProviders);
      }
    }, [serviceProviderDetails, setValue, getValues]);

    const handleInstallerChange = (
      event: ChangeEvent<HTMLInputElement>,
      checked: boolean
    ) => {
      const currentSelected = getValues(
        "selectedServiceProviders"
      ) as SelectedProvider[];

      const updatedProviders = checked
        ? [...currentSelected, { id: event.target.value }]
        : currentSelected.filter(
            (val: SelectedProvider) => val.id !== event.target.value
          );

      setValue("selectedServiceProviders", updatedProviders);
    };

    const getInstallerName = (index: number) => `Installer ${index + 1}`;

    const isProviderChecked = (id: string) => {
      const selectedServiceProviders: SelectedProvider[] = getValues(
        "selectedServiceProviders"
      );
      if (!selectedServiceProviders || !selectedServiceProviders.length) {
        return true;
      }
      return selectedServiceProviders.some((item) => item.id === id);
    };

    return (
      <>
        <FlexBox marginTop="medium" marginBottom="large">
          {serviceProviderDetails.map((item, index) => (
            <InstallerCheckbox
              providerDetail={item}
              data-id={item.id}
              key={item.id}
              id={item.id}
              label={getInstallerName(index)}
              checked={isProviderChecked(item.id)}
              value={item.id}
              onChange={handleInstallerChange}
            />
          ))}
        </FlexBox>

        <Box marginTop="xlarge">
          <Text variant="body-small">
            {content.consentInfoIntro}
            <TextLink href={content.termOfUseLink}>
              {content.termOfUseLinkText}
            </TextLink>
            {content.consentInfoMiddle}
            <TextLink href={content.consentLink}>
              {content.consentInfoLinkText}
            </TextLink>
            {content.consentInfoEnd}
            {content.contactPreferenceIntro}
            <TextLink href={content.contactPreferenceLink}>
              {content.contactPreferenceLinkText}
            </TextLink>
            {content.contactPreferenceEnd}
          </Text>
        </Box>
      </>
    );
  }
);
